import { Dashboard, DataType, Orientation, Sorting } from "../../types";
import { StyleSheet, View } from "react-native";

import { Metrics } from "../../resources";
import React from "react";
import SHeaderTitle from "../atoms/SHeaderTitle";
import SSpacer from "../atoms/SSpacer";
import STooltip from "./STooltip";
import { useSettings } from "../../hooks";

interface IProps {
  type: Dashboard;
}

const SListHeader: React.FC<IProps> = ({ type }) => {
  const { settings, writeSetting } = useSettings();

  const onPressSort = (type: DataType) => {
    const { order, sort } = settings || {};
    const reverseOrder =
      settings?.order === Sorting.Incremental
        ? Sorting.Decremental
        : Sorting.Incremental;
    const sortOrder =
      sort === type ? reverseOrder : order || Sorting.Incremental;

    writeSetting({
      sort: type,
      order: sortOrder,
    });
  };

  const sorted = (type: DataType) => {
    if (!settings?.order) {
      return Sorting.None;
    }
    if (settings?.sort === type) {
      return settings?.order;
    }
  };

  const onAddressPressSort = () => {
    onPressSort(DataType.Name);
  };

  const onCapacityPressSort = () => {
    settings?.capacity === DataType.LegalCapacity
      ? onPressSort(DataType.LegalCapacity)
      : onPressSort(DataType.TargetCapacity);
  };

  const onSafeCapacityPressSort = () => {
    settings?.safe === DataType.SafeCapacityPpl
      ? onPressSort(DataType.SafeCapacityPpl)
      : onPressSort(DataType.SafeCapacityPercentage);
  };

  const onBusiestPressSort = () => {
    settings?.busiest === DataType.BusiestPpl
      ? onPressSort(DataType.BusiestPpl)
      : onPressSort(DataType.BusiestPercentage);
  };

  const onAveragePressSort = () => {
    settings?.average === DataType.AveragePpl
      ? onPressSort(DataType.AveragePpl)
      : onPressSort(DataType.AveragePercentage);
  };

  const renderTableHeader = () => {
    return (
      <>
        <STooltip
          title={
            settings?.busiest === DataType.BusiestPpl
              ? "Busiest Occupancy Number"
              : "Busiest Occupancy Percentage"
          }
          content={
            settings?.busiest === DataType.BusiestPpl
              ? "The highest number of people ever occupying\n the space during the selected time period."
              : "The highest number of people ever occupying\n the space during the selected time period,\n as percentage of capacity."
          }
          placement="top"
        >
          <SHeaderTitle
            label="BUSIEST"
            unit={settings?.busiest === DataType.BusiestPpl ? "(ppl)" : "(%)"}
            sorted={
              settings?.busiest === DataType.BusiestPpl
                ? sorted(DataType.BusiestPpl)
                : sorted(DataType.BusiestPercentage)
            }
            onPress={onBusiestPressSort}
          />
        </STooltip>

        <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
        <STooltip
          title={
            settings?.average === DataType.AveragePpl
              ? "Average Occupancy Number"
              : "Average Occupancy Percentage"
          }
          content={
            settings?.average === DataType.AveragePpl
              ? "The average number of people occupying\n the space during the selected time period."
              : "The average number of people occupying\n the space during the selected time period,\n as percentage of capacity."
          }
          placement="top"
        >
          <SHeaderTitle
            label="AVERAGE"
            unit={settings?.average === DataType.AveragePpl ? "(ppl)" : "(%)"}
            sorted={
              settings?.average === DataType.AveragePpl
                ? sorted(DataType.AveragePpl)
                : sorted(DataType.AveragePercentage)
            }
            onPress={onAveragePressSort}
          />
        </STooltip>
        <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
        <View style={styles.columnTag}>
          <STooltip
            title="Tags"
            content="Properties set for each location."
            placement="top"
          >
            <SHeaderTitle label="TAGS" unit="" disabled />
          </STooltip>
        </View>
        <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
      </>
    );
  };

  const renderChartHeader = () => {
    return (
      <>
        <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
        <STooltip
          title="Daily Peak Occupancy"
          content={
            "The highest number of people ever occupying\n the space each day."
          }
          placement="top"
        >
          <SHeaderTitle label="PEAK OCCUPANCY" unit="(by day)" />
        </STooltip>
      </>
    );
  };

  return (
    <View style={styles.container}>
      <SSpacer type={Orientation.Horizontal} space={Metrics.padding8} />
      <View style={styles.colomn1Title}>
        <STooltip
          title="Location"
          content="Location name and address."
          placement="top"
        >
          <SHeaderTitle
            label="LOCATION"
            unit=""
            sorted={sorted(DataType.Name)}
            onPress={onAddressPressSort}
          />
        </STooltip>
      </View>
      <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
      <STooltip
        title={
          settings?.capacity === DataType.LegalCapacity
            ? "Legal Capacity"
            : "Target Capacity"
        }
        content={
          settings?.capacity === DataType.LegalCapacity
            ? "Number of people legally allowed in the space."
            : "Number of people the space is designed for."
        }
        placement="top"
      >
        <SHeaderTitle
          label={
            settings?.capacity === DataType.LegalCapacity
              ? "LEGAL CAPACITY"
              : "TARGET CAPACITY"
          }
          unit=""
          width={Metrics.size108}
          sorted={
            settings?.capacity === DataType.LegalCapacity
              ? sorted(DataType.LegalCapacity)
              : sorted(DataType.TargetCapacity)
          }
          onPress={onCapacityPressSort}
        />
      </STooltip>
      <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
      <STooltip
        title={
          settings?.safe === DataType.SafeCapacityPpl
            ? "Safe Number"
            : "Safe Percentage"
        }
        content={
          settings?.safe === DataType.SafeCapacityPpl
            ? "Number of people allowed for safety."
            : "Percentage of capacity allowed for safety."
        }
        placement="top"
      >
        <SHeaderTitle
          label="SAFE"
          unit={settings?.safe === DataType.SafeCapacityPpl ? "(ppl)" : "(%)"}
          sorted={
            settings?.safe === DataType.SafeCapacityPpl
              ? sorted(DataType.SafeCapacityPpl)
              : sorted(DataType.SafeCapacityPercentage)
          }
          onPress={onSafeCapacityPressSort}
        />
      </STooltip>

      {type === Dashboard.Table ? (
        <SSpacer type={Orientation.Horizontal} space={Metrics.padding24} />
      ) : null}
      {type === Dashboard.Table ? renderTableHeader() : renderChartHeader()}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: "row",
    alignItems: "center",
    marginVertical: 2,
    height: Metrics.size24,
    marginBottom: Metrics.padding8,
  },
  column1: {
    width: Metrics.size300,
    flexDirection: "row",
    alignItems: "center",
    justifyContent: "flex-start",
    paddingLeft: Metrics.iconSmall,
  },
  colomn1Title: {
    alignItems: "flex-start",
    marginLeft: Metrics.iconSmall + Metrics.paddingSmallStandard,
    width: Metrics.size300 - Metrics.iconSmall - Metrics.paddingSmallStandard,
  },
  column: {
    width: Metrics.size64,
    justifyContent: "center",
    alignItems: "center",
  },
  columnTag: {
    width: Metrics.size96,
    marginLeft: Metrics.padding16,
    justifyContent: "center",
    alignItems: "flex-start",
  },
});

export default SListHeader;
